import React, {useCallback, useEffect, useRef, useState} from 'react';
import ReactPlayer from 'react-player';
import cn from 'classnames';

import {IThronVideoProps} from './IThronVideo';
import {MuteButton, PlayButton} from './Subcomponents';

import styles from './ThronVideo.module.scss';

const ThronVideo = ({thronVideo, videoConfig, controlsConfig}: IThronVideoProps) => {
	const {showControls = false, autoplay = true, muted = true, loop = true} = videoConfig;
	const isAutoplay = autoplay && muted;

	const wrapperRef = useRef(null);
	const thumbnailRef = useRef(null);

	const src = thronVideo.getVideoUrl();
	const thumbnailUrl = thronVideo.getThumbnailUrl();
	const aspectRatio = thronVideo.getAspectRatio();

	const [isVisible, setIsVisible] = useState<boolean>(false);
	const [initializeVideo, setInitializeVideo] = useState<boolean>(false);
	const [isPlaying, setIsPlaying] = useState<boolean>(isAutoplay);
	const [isMuted, setIsMuted] = useState<boolean>(muted);

	const toggleThumbnail = useCallback((show) =>{
		thumbnailRef.current.classList.toggle('d-none', !show);
	}, []);

	useEffect(() => {
		setIsPlaying(isVisible && autoplay);
	}, [isVisible]);

	useEffect(() => {
		const options = {
			threshold: 0.01,
			rootMargin: '0px',
		};

		const observer = new IntersectionObserver(([entry]) => {
			if (entry.isIntersecting) {
				setIsVisible(true);
				setInitializeVideo(true);
			} else {
				setIsVisible(false);
			}
		}, options);

		if (wrapperRef.current) {
			observer.observe(wrapperRef.current);
		}

		return () => {
			if (wrapperRef.current) {
				observer.unobserve(wrapperRef.current);
			}
		};
	}, []);

	const APIOptions = {
		playerOptions: {
			controls: true,
			responsive: true,
			fullscreen: false
		}
	};

	return (
		<div className={cn(styles['thron-video-wrapper'], 'thron-video-wrapper')} key={src} ref={wrapperRef}>
			<div className={cn(styles['thron-video-thumbnail'], 'thron-video-thumbnail')}  ref={thumbnailRef}>
				<img src={thumbnailUrl} />
			</div>
			<div
				className={cn(styles['video-embed'], 'video-embed')}
				style={{aspectRatio}}
			>
				{initializeVideo &&
					<ReactPlayer
						url={src}
						playing={isPlaying}
						loop={loop}
						muted={muted}
						volume={+!isMuted}
						onReady={!isAutoplay ? (() => toggleThumbnail(false)) : undefined}
						onStart={isAutoplay ? (() => toggleThumbnail(false)) : undefined}
						config={APIOptions}
						height="100%"
						width="100%"
						playsinline
					/>
				}
			</div>
			<div className={cn(styles['thron-video-overlay'], 'thron-video-overlay')}>
				{showControls && (
					<div className={cn(styles['thron-video-controls'], 'thron-video-controls')}>
						{controlsConfig?.playButton && (
							<PlayButton
								setIsPlaying={setIsPlaying}
								isPlaying={isPlaying}
								icons={{
									play: controlsConfig.playButton.playIcon,
									pause: controlsConfig.playButton.pauseIcon,
									height: controlsConfig.playButton.height,
									width: controlsConfig.playButton.width,
								}}
							/>
						)}
						{controlsConfig?.muteButton && (
							<MuteButton
								setIsMuted={setIsMuted}
								isMuted={isMuted}
								icons={{
									muted: controlsConfig.muteButton.mutedIcon,
									unmuted: controlsConfig.muteButton.unmutedIcon,
									height: controlsConfig.muteButton.height,
									width: controlsConfig.muteButton.width,
								}}
							/>
						)}
					</div>
				)}
			</div>
		</div>
	);
};

export default ThronVideo;
